<div class="mb-6">
  <div class="mb-2 flex items-center justify-between">
    <p class="text-xs font-semibold uppercase tracking-wide text-slate-400">Diagnosis</p>
    <span *ngIf="lastUpdated" class="text-[11px] text-slate-400">Updated {{ lastUpdated | date:'MMM d, h:mm a' }}</span>
  </div>

  <!-- Loading -->
  <div *ngIf="loading" class="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-500">
    <lucide-icon name="loader" [size]="14" [strokeWidth]="2" class="animate-spin"></lucide-icon>
    Analyzing this finding…
  </div>

  <ng-container *ngIf="!loading">
    <!-- Concrete diagnosis -->
    <div
      *ngIf="hasConcreteDiagnosis; else nonConcrete"
      class="rounded-lg border border-slate-200 bg-white px-4 py-4 text-sm"
    >
      <!-- Category + confidence -->
      <div class="mb-3 flex flex-wrap items-center gap-2">
        <span class="inline-flex items-center rounded-md bg-violet-50 px-2 py-1 text-xs font-semibold text-violet-700">
          {{ categoryLabel(d?.category) }}
        </span>
        <span class="inline-flex items-center rounded-md px-2 py-1 text-xs font-semibold" [ngClass]="confidenceBadgeClass">
          {{ d?.confidence | titlecase }} confidence · {{ confidencePct }}%
        </span>
      </div>

      <!-- Prose summary (optional, non-authoritative) -->
      <p *ngIf="proseSummary" class="mb-3 leading-relaxed text-slate-700">{{ proseSummary }}</p>

      <dl class="space-y-3">
        <div *ngIf="d?.observedSymptom">
          <dt class="text-xs font-medium uppercase tracking-wide text-slate-400">Observed symptom</dt>
          <dd class="mt-0.5 text-slate-700">{{ d?.observedSymptom }}</dd>
        </div>
        <div *ngIf="d?.likelyCause">
          <dt class="text-xs font-medium uppercase tracking-wide text-slate-400">Likely cause</dt>
          <dd class="mt-0.5 text-slate-700">{{ d?.likelyCause }}</dd>
        </div>
        <div *ngIf="d?.topSignature as sig">
          <dt class="text-xs font-medium uppercase tracking-wide text-slate-400">
            Top error signature<span *ngIf="sig.count > 1" class="font-normal normal-case text-slate-400"> · {{ sig.count }}×</span>
          </dt>
          <dd class="mt-1 overflow-x-auto whitespace-pre-wrap break-words rounded-md bg-slate-900 px-3 py-2 font-mono text-[11px] leading-relaxed text-slate-100">{{ sig.sample }}</dd>
        </div>
        <div *ngIf="!d?.topSignature && d?.likelyError">
          <dt class="text-xs font-medium uppercase tracking-wide text-slate-400">Top error signature</dt>
          <dd class="mt-1 overflow-x-auto whitespace-pre-wrap break-words rounded-md bg-slate-900 px-3 py-2 font-mono text-[11px] leading-relaxed text-slate-100">{{ d?.likelyError }}</dd>
        </div>
        <div *ngIf="d?.suggestedNextSteps?.length">
          <dt class="text-xs font-medium uppercase tracking-wide text-slate-400">Suggested next steps</dt>
          <dd class="mt-1">
            <ul class="list-disc space-y-1 pl-4 text-slate-700">
              <li *ngFor="let step of d?.suggestedNextSteps">{{ step }}</li>
            </ul>
          </dd>
        </div>
      </dl>
    </div>

    <!-- Non-concrete states -->
    <ng-template #nonConcrete>
      <!-- Inconclusive: logs found, no known pattern -->
      <div
        *ngIf="state === 'inconclusive'"
        class="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800"
      >
        <p class="font-medium">Inconclusive</p>
        <p class="mt-0.5 text-amber-700">Correlated logs were found but did not match a known failure pattern, so no confident diagnosis could be made.</p>
        <p *ngIf="d?.observedSymptom" class="mt-2 text-amber-700"><span class="font-medium">Observed:</span> {{ d?.observedSymptom }}</p>
      </div>

      <!-- No evidence: no correlated logs -->
      <div
        *ngIf="state === 'no_evidence'"
        class="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600"
      >
        <p class="font-medium text-slate-700">No correlated logs found</p>
        <p class="mt-0.5">No log evidence could be correlated to this finding within the search window, so a root-cause diagnosis wasn't possible.</p>
      </div>

      <!-- Pending -->
      <div
        *ngIf="state === 'pending'"
        class="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-500"
      >
        <lucide-icon name="loader" [size]="14" [strokeWidth]="2" class="animate-spin"></lucide-icon>
        Diagnosis in progress — check back shortly.
      </div>

      <!-- Failed / unavailable -->
      <div
        *ngIf="state === 'failed'"
        class="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-500"
      >
        Diagnosis is currently unavailable for this finding.
      </div>

      <!-- Not found: no enrichment record at all -->
      <div
        *ngIf="state === 'not_found'"
        class="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-400"
      >
        No diagnosis available for this finding.
      </div>
    </ng-template>
  </ng-container>
</div>
